import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronDown, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }
});

const FAQ_ITEMS = [
  { q: "Vad är Emotionell Frihet?", a: "Emotionell frihet handlar om att leva i kontakt med sig själv, utan att omedvetet styras av tidigare erfarenheter, invanda mönster eller obearbetade känslor. Det innebär inte att bli fri från känslor — utan att kunna vara i dem utan att fastna." },
  { q: "Hur fungerar metoden?", a: "Metoden är uppbyggd i tre faser (Klarhet, Frigörelse, Förankring) med totalt nio steg. Varje steg innehåller fördjupande texter, reflektionsfrågor och guidade övningar. Du arbetar dig igenom dem i din egen takt." },
  { q: "Hur lång tid tar det?", a: "Det finns ingen tidsgräns. Vissa gör ett steg per vecka, andra tar längre tid. Det viktiga är inte hastigheten utan att du verkligen tar in det du läser och reflekterar över." },
  { q: "Kan jag göra om stegen?", a: "Ja, absolut. När du har slutfört ett steg kan du alltid gå tillbaka och läsa det igen, uppdatera dina reflektioner eller göra övningen på nytt." },
  { q: "Behöver jag någon förkunskap?", a: "Nej, ingen förkunskap behövs. Metoden är skriven för alla som vill förstå sig själva bättre, oavsett var du befinner dig." },
  { q: "Vad händer efter betalningen?", a: "Du får direkt tillgång till hela metoden. Först gör du en kort incheckning med dig själv, sedan börjar du med det första steget i Fas 1: Klarhet." },
  { q: "Är mina reflektioner privata?", a: "Ja. Allt du skriver — reflektioner, dagbok och testresultat — är helt privat och tillgängligt bara för dig." },
  { q: "Kan jag använda metoden på mobilen?", a: "Ja, plattformen fungerar på mobil, surfplatta och dator. Du kan även installera den som en app på din telefon." },
  { q: "Vad kostar det?", a: "Metoden kostar 199 kr som en engångsbetalning. Inga prenumerationer eller dolda kostnader. Du betalar en gång och får tillgång för alltid." },
  { q: "Hur betalar jag?", a: "Du kan betala med kort, Swish eller Klarna via vår säkra betalningslösning (Stripe)." },
  { q: "Finns det en bok kopplad till metoden?", a: "Ja, boken 'Din väg till emotionell frihet' är skriven som ett komplement till appen. Boken ger dig förståelsen, appen ger dig praktiken. Du kan beställa boken via leiladadgar.se." },
  { q: "Hur kontaktar jag dig?", a: "Du kan nå mig via e-post på leila.dadgar@expressa.se eller besöka leiladadgar.se för mer information om mig och mitt arbete." },
];

export default function FAQPage() {
  const [openIndex, setOpenIndex] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-white" data-testid="faq-page">
      <Navigation />
      <div className="pt-28 sm:pt-36 pb-16 sm:pb-24 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          <motion.div {...fadeUp()} className="text-center mb-10 sm:mb-14">
            <p className="text-xs sm:text-sm uppercase tracking-[0.25em] font-medium text-[#A0655A] mb-4">
              Vanliga frågor
            </p>
            <h1 className="heading-font text-3xl sm:text-4xl lg:text-5xl font-medium text-[#111111] mb-3 leading-tight" data-testid="faq-title">
              Allt du behöver veta
            </h1>
            <p className="text-[#111111]/50 text-sm max-w-md mx-auto leading-relaxed">
              Här har jag samlat de vanligaste frågorna om metoden och plattformen.
            </p>
          </motion.div>

          <div className="space-y-2.5" data-testid="faq-accordion">
            {FAQ_ITEMS.map((item, i) => (
              <motion.div key={i} {...fadeUp(0.03 * i)}>
                <button
                  onClick={() => setOpenIndex(openIndex === i ? null : i)}
                  className={`w-full flex items-center justify-between p-4 sm:p-5 rounded-xl border text-left transition-all duration-200 ${
                    openIndex === i
                      ? 'bg-[#FDF9F7] border-[#EBE3DF]'
                      : 'bg-white border-[#EBE3DF]/50 hover:border-[#EBE3DF]'
                  }`}
                  data-testid={`faq-item-${i}`}
                >
                  <span className="text-sm font-medium text-[#111111] pr-4">{item.q}</span>
                  <ChevronDown
                    size={16}
                    className={`text-[#A0655A] flex-shrink-0 transition-transform duration-200 ${openIndex === i ? 'rotate-180' : ''}`}
                  />
                </button>
                {openIndex === i && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="px-4 sm:px-5 pb-4 pt-2"
                  >
                    <p className="text-sm text-[#111111]/55 leading-relaxed">{item.a}</p>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          {!user?.has_paid && (
            <motion.div {...fadeUp(0.2)} className="text-center mt-14 pt-8 border-t border-[#EBE3DF]/50">
              <p className="text-[#111111]/50 text-sm mb-5">Har du fler frågor? Du kan alltid kontakta mig.</p>
              <Button onClick={() => navigate(user ? '/betalning' : '/registrera')}
                className="bg-[#A0655A] hover:bg-[#8B5549] text-white rounded-full px-8 py-5 text-base"
                data-testid="faq-cta-btn">
                {user ? 'Gå till betalning' : 'Börja din resa'}
                <ArrowRight className="ml-2" size={16} />
              </Button>
            </motion.div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
